import type { TeamResponse } from "../types/api";

const STORAGE_KEY_PREFIX = "teamChips";

/**
 * Get the localStorage key for team chips for a specific organization
 */
const getStorageKey = (organizationId: string): string => {
  return `${STORAGE_KEY_PREFIX}_${organizationId}`;
};

/**
 * Get stored team chip IDs for the current organization
 * Returns empty array if none found or on error
 */
export const getStoredTeamChipIds = (
  organizationId: string | null
): string[] => {
  if (!organizationId) {
    return [];
  }

  try {
    const key = getStorageKey(organizationId);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    // Validate that the parsed data is an array
    if (!Array.isArray(parsed)) {
      console.warn(
        `Invalid team chips data in localStorage for org ${organizationId}`
      );
      return [];
    }

    return parsed;
  } catch (error) {
    console.warn("Failed to load team chips from localStorage:", error);
    return [];
  }
};

/**
 * Save team chip IDs to localStorage for the current organization
 * Handles null/empty organization ID gracefully
 */
export const saveTeamChipIds = (
  organizationId: string | null,
  teamIds: string[]
): void => {
  if (!organizationId) {
    return;
  }

  try {
    const key = getStorageKey(organizationId);
    localStorage.setItem(key, JSON.stringify(teamIds));
  } catch (error) {
    console.warn("Failed to save team chips to localStorage:", error);
  }
};

/**
 * Clear stored team chips for the current organization
 */
export const clearTeamChipIds = (organizationId: string | null): void => {
  if (!organizationId) {
    return;
  }

  try {
    const key = getStorageKey(organizationId);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("Failed to clear team chips from localStorage:", error);
  }
};

/**
 * Clear ALL team chips from localStorage (for all organizations)
 * Used during logout to prevent data leakage
 */
export const clearAllTeamChips = (): void => {
  try {
    const keysToRemove: string[] = [];

    // Find all keys that start with the team chips prefix
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    // Remove all team chip keys
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("Failed to clear all team chips from localStorage:", error);
  }
};

/**
 * Rehydrate full TeamResponse objects from stored IDs
 * Filters out IDs that don't match any team in the provided array
 */
export const rehydrateTeamChips = (
  teamIds: string[],
  allTeams: TeamResponse[]
): TeamResponse[] => {
  if (teamIds.length === 0 || allTeams.length === 0) {
    return [];
  }

  return teamIds
    .map((id) => allTeams.find((team) => team.id === id))
    .filter((team): team is TeamResponse => team !== undefined);
};
